import React, { useState, useRef, useEffect } from "react";
import { Modal, Form, Button, Badge, Image, Spinner } from "react-bootstrap";
import { CircleX } from "lucide-react";
import { ReactComponent as ImageIcon } from "../../../../assets/images/upload-img.svg";
import {
  createPostFormData,
  validateImageFile,
  normalizeNbsp,
} from "../../utils/communityHelpers";
import "./CreatePostModal.scss";
import { FiX } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { getPreviewLink } from "../../../../redux/slices/editPostSlice";
import {
  activityEnd,
  activityStart,
  activityPing,
} from "../../../../redux/slices/trackActivitySlice";
import usePageTracking from "../../../../hooks/usePageTracking";
import Toast from "../../../../components/toasts";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import "../../utils/quillPasteClipboard";

/**
 * CreatePostModal Component
 * Modal for creating a new community post
 * Handles image upload, category selection, and anonymous posting
 * Integrates with API via parent component callback
 *
 * @param {boolean} show - Whether modal is visible
 * @param {function} onHide - Callback to close modal
 * @param {function} createPost - Function to create post (from usePosts hook)
 * @param {Array} categories - Available post categories
 * @param {boolean} isLoading - Categories loading state
 * @param {string} initialCategory - Initial category name to select
 */
const CreatePostModal = ({
  show,
  onHide,
  createPost,
  categories,
  isLoading: categoriesLoading,
  initialCategory,
  onpostSubmit,
  onCategoryChange,
  preFilledContent,
  initialShowPoll,
}) => {
  // ============= STATE =============
  const [postContent, setPostContent] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const [fileError, setFileError] = useState("");

  // Poll state
  const [showPollForm, setShowPollForm] = useState(false);
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [isMultiSelect, setIsMultiSelect] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [linkPreview, setLinkPreview] = useState(null);
  const [isPreviewPending, setIsPreviewPending] = useState(false);
  const [postTitle, setPostTitle] = useState("");
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const previewTimeoutRef = useRef(null);
  const dispatch = useDispatch();

  // const extractUrl = (text) => {
  // const urlRegex = /((https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*))/g;
  //   const match = text.match(urlRegex);
  //   return match ? match[0] : null;
  // };
  const extractUrl = (text) => {
    if (!text) return null;

    const urlRegex = /((https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*))/gi;

    const match = text.match(urlRegex);
    if (!match || !match[0]) return null;

    let url = match[0].trim();

    // Add https:// if protocol is missing
    if (!/^https?:\/\//i.test(url)) {
      url = `https://${url}`;
    }

    try {
      const parsedUrl = new URL(url);

      // Ensure trailing slash for root domains
      if (!parsedUrl.pathname || parsedUrl.pathname === "") {
        parsedUrl.pathname = "/";
      }

      return parsedUrl.toString();
    } catch {
      return null;
    }
  };

  const stripHtml = (html) => {
    if (!html) return "";
    // Insert a space before closing block tags so adjacent paragraphs/list items
    // don't concatenate (e.g. "stood out.</p><p>Here" → "stood out. Here")
    // preventing the regex from picking up false URL tokens like "out.Here".
    const spaced = html
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, " ");
    const div = document.createElement("div");
    div.innerHTML = spaced;
    return div.textContent || "";
  };

  useEffect(() => {
    const url = extractUrl(stripHtml(postContent));

    if (!url) {
      setLinkPreview(null);
      setIsPreviewPending(false);
      setIsPreviewLoading(false);
      return;
    }

    setIsPreviewPending(true);

    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }

    previewTimeoutRef.current = setTimeout(async () => {
      try {
        setIsPreviewLoading(true);

        const res = await dispatch(getPreviewLink({ url })).unwrap();
        setLinkPreview(res?.editPost || null);
      } catch {
        setLinkPreview(null);
      } finally {
        setIsPreviewLoading(false);
        setIsPreviewPending(false);
      }
    }, 2000);

    return () => clearTimeout(previewTimeoutRef.current);
  }, [postContent, dispatch]);

  // NEW: Set pre-filled content when modal opens with it
  useEffect(() => {
    if (show && preFilledContent) {
      setPostContent(preFilledContent);
    }
  }, [preFilledContent, show]);

  useEffect(() => {
    if (show && initialShowPoll) {
      setShowPollForm(true);
    }
  }, [show, initialShowPoll]);

  const isValidPreview = (preview) => {
    if (!preview) return false;

    const { title, description, image, domain } = preview;

    return (
      Boolean(title?.trim()) ||
      Boolean(description?.trim()) ||
      Boolean(image?.trim()) ||
      Boolean(domain?.trim())
    );
  };

  useEffect(() => {
    setSelectedCategory(initialCategory);
  }, [initialCategory, show]);

  // Conditionally track page only when modal is shown
  
  // Manual page tracking that only runs when modal is open
  useEffect(() => {
    if (!show) return;

    const PING_INTERVAL = 30000; // 30 seconds
    const startTimeRef = { current: null };
    let intervalRef = null;

    const sendData = () => {
      if (!startTimeRef.current) return;

      const now = Date.now();
      const timeSpentMs = now - startTimeRef.current;

      startTimeRef.current = now;

      if (timeSpentMs < 100) return;

      const payload = {
        page_name: "CREATE_POST",
      };

      dispatch(activityPing(payload));
    };

    // Initialize tracking when modal opens
    if (!document.hidden) {
      startTimeRef.current = Date.now();
      intervalRef = setInterval(sendData, PING_INTERVAL);
    } else {
      startTimeRef.current = null;
    }

    // Handle Visibility Change
    const handleVisibilityChange = () => {
      if (document.hidden) {
        sendData();
        if (intervalRef) clearInterval(intervalRef);
        startTimeRef.current = null;
      } else {
        startTimeRef.current = Date.now();
        if (intervalRef) clearInterval(intervalRef);
        intervalRef = setInterval(sendData, PING_INTERVAL);
      }
    };

    // Handle Page Unload
    const handleBeforeUnload = () => {
      sendData();
    };

    // Event Listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup when modal closes
    return () => {
      sendData();
      if (intervalRef) clearInterval(intervalRef);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [show, dispatch]);

  useEffect(() => {
    if (!show) return;

    dispatch(
      activityStart({
        page_name: "CREATE_POST",
        platform: "web",
      }),
    );
  }, [show, dispatch]);

  useEffect(() => {
    if (!show) return;

    return () => {
      dispatch(
        activityEnd({
          page_name: "CREATE_POST",
        }),
      );
    };
  }, [show, dispatch]);

   // Added 'show' dependency

  // ============= EFFECTS =============
  /**
   * Set default category when modal opens (show changes to true)
   */
  React.useEffect(() => {
    if (show && categories && categories.length > 0) {
      // If initialCategory is provided, find and set that category
      if (initialCategory) {
        const matchingCategory = categories.find(
          (cat) => cat.category_name === initialCategory,
        );
        if (matchingCategory) {
          setActiveCategory(matchingCategory);
          return;
        }
      }
      // Otherwise, set first category as default
      setActiveCategory(categories[0]);
    }
  }, [show, categories, initialCategory]);

  // ============= HANDLERS =============
  /**
   * Handle file selection
   * Validates file and creates preview
   */
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Validate each file
    const validFiles = [];
    const validPreviews = [];

    for (const file of files) {
      const validation = validateImageFile(file);
      if (!validation.valid) {
        setFileError(validation.error);
        Toast.showError(validation.error);
        continue;
      }

      validFiles.push(file);

      // Create preview for images
      const isHeic = file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');
      
      if (isHeic) {
        // For HEIC files, show a placeholder since browsers can't preview them
        validPreviews.push("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect width='200' height='200' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='14' fill='%23666'%3EHEIC Image%3C/text%3E%3C/svg%3E");
      } else if (file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file);
        validPreviews.push(url);
      } else {
        validPreviews.push(null);
      }
    }

    // Update state with valid files
    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
      setPreviewUrls((prev) => [...prev, ...validPreviews]);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  setTimeout(() => {
    setFileError("");
  }, 3000);

  /**
   * Clear specific file
   */
  const clearFile = (index) => {
    // Revoke object URL to prevent memory leak
    if (previewUrls[index]) {
      URL.revokeObjectURL(previewUrls[index]);
    }

    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Clear all files
   */
  const clearAllFiles = () => {
    // Revoke all object URLs
    previewUrls.forEach((url) => {
      if (url) URL.revokeObjectURL(url);
    });

    setSelectedFiles([]);
    setPreviewUrls([]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /**
   * Handle form submission with optimistic UI update
   * Creates FormData and calls createPost from usePosts hook
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event from bubbling to parent elements

    // Validation
    if (!postContent?.trim()) {
      Toast.showError("Please enter post content");
      return;
    }
    if (!postTitle?.trim()) {
      Toast.showError("Please enter post title");
      return;
    }

    // Poll validation
    if (showPollForm) {
      if (!pollQuestion.trim()) {
        Toast.showError("Please enter a poll question");
        return;
      }
      const validPollOptions = pollOptions.filter((o) => o.trim());
      if (validPollOptions.length < 2) {
        Toast.showError("Please enter at least 2 poll options");
        return;
      }
    }

    if (!activeCategory) {
      Toast.showError("Please select a category");
      return;
    }

    if (!createPost) {
      console.error("createPost function not provided to CreatePostModal");
      Toast.showError("Unable to create post. Please try again.");
      return;
    }

    try {
      setIsSubmitting(true);

      // Create FormData using helper
      const formData = createPostFormData({
        title: postTitle,
        content: normalizeNbsp(postContent).trim(),
        category: activeCategory.id,
        isAnonymous,
        images: selectedFiles,
        link_preview: linkPreview || {},
      });

      // Attach poll data if user created a poll
      if (showPollForm && pollQuestion.trim()) {
        const validPollOptions = pollOptions.filter((o) => o.trim());
        if (validPollOptions.length >= 2) {
          formData.append("poll_question", pollQuestion.trim());
          formData.append("poll_options", JSON.stringify(validPollOptions));
          formData.append("poll_is_multi_select", isMultiSelect ? "true" : "false");
        }
      }

      const result = await createPost(formData, activeCategory.category_name);

      if (result?.success) {
        // Reset form and close modal
        resetForm();
        onHide();
        onpostSubmit();
      } else {
        console.error(
          "❌ [CREATE POST MODAL] Post creation failed:",
          result?.error,
        );
        // Error toast is shown by createPost
      }
    } catch (error) {
      console.error("❌ [CREATE POST MODAL] Unexpected error:", error);
      Toast.showError("Failed to create post. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Reset form to initial state
   */
  const resetForm = () => {
    setPostContent("");
    setPostTitle("");
    clearAllFiles();
    setIsAnonymous(false);
    setShowPollForm(false);
    setPollQuestion("");
    setPollOptions(["", ""]);
    setIsMultiSelect(false);
    // Reset to first category
    if (categories && categories.length > 0) {
      setActiveCategory(categories[0]);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (isSubmitting) return; // Prevent closing while submitting

    resetForm();
    onHide();
  };

  // ============= COMPUTED VALUES =============
  // const isFormValid = postContent?.trim() && activeCategory && !isSubmitting;
  const isFormValid =
    postContent?.trim() &&
    activeCategory &&
    !isSubmitting &&
    !isPreviewLoading &&
    !isPreviewPending;

  const displayCategories = Array.isArray(categories) ? categories : [];

  // ============= RENDER =============
  return (
    <Modal
      show={show}
      onHide={handleClose}
      className="create-post-modal"
      centered
      size="lg"
      backdrop={isSubmitting ? "static" : true}
      keyboard={!isSubmitting}
      dialogClassName="custom-modal"
    >
      <Modal.Header>
        <h5 className="modal-title">Create a post</h5>
        <Button
          className="close-btn"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          <FiX size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body className="pt-2">
        <Form onSubmit={handleSubmit}>
          {/* Category Selection */}
          <div className="category-section">
            <h6 className="category-title">Choose category</h6>
            {categoriesLoading ? (
              <div className="text-center py-2">
                <Spinner animation="border" size="sm" />
                <small className="ms-2">Loading categories...</small>
              </div>
            ) : (
              <div className="category-tabs">
                {displayCategories.map((category) => (
                  <Badge
                    key={category?.id}
                    bg={
                      activeCategory?.id === category?.id ? "primary" : "light"
                    }
                    text={
                      activeCategory?.id === category?.id ? "white" : "dark"
                    }
                    className={`category-tab ${
                      activeCategory?.id === category?.id ? "active" : ""
                    }`}
                    // onClick={() => setActiveCategory(category)}
                    onClick={() => {
                      setActiveCategory(category);
                      onCategoryChange?.(category.category_name);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    {category?.category_name}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Post Content */}
          <Form.Group className="form-group">
            <Form.Label className="col-label">Post Title</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Your Title"
              className="col-input"
              value={postTitle}
              onChange={(e) => setPostTitle(e.target.value)}
              required
            />
          </Form.Group>
          <div className="post-content-section">
            <div className="quill-wrapper">
              <ReactQuill
                theme="snow"
                value={postContent}
                onChange={setPostContent}
                readOnly={isSubmitting}
                placeholder="Share your experience..."
              />

              {/* Link Preview */}
              {isPreviewLoading && !isValidPreview(linkPreview) && (
                <div className="link-preview loading">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Fetching link preview...</span>
                </div>
              )}

              {!isPreviewLoading && isValidPreview(linkPreview) && (
                <div className="link-preview-card p-2 border rounded position-relative">
                  <button
                    type="button"
                    className="btn-close position-absolute top-0 end-0 m-2"
                    onClick={() => setLinkPreview(null)}
                  />

                  <div className="d-flex gap-2">
                    {linkPreview.image && (
                      <Image
                        src={linkPreview.image}
                        width={80}
                        height={80}
                        className="rounded"
                        style={{ objectFit: "cover" }}
                      />
                    )}

                    <div className="flex-grow-1">
                      {linkPreview.title && (
                        <h6 className="mb-1 text-truncate">
                          {linkPreview.title}
                        </h6>
                      )}

                      {linkPreview.description && (
                        <p className="mb-0 text-muted small line-clamp-2">
                          {linkPreview.description}
                        </p>
                      )}

                      {linkPreview.domain && (
                        <small className="text-primary">
                          {linkPreview.domain}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Footer: image upload inside the box */}
              <div className="quill-footer">
                <button
                  type="button"
                  className="media-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                  title="Upload image"
                >
                  <ImageIcon className="upload-icon" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.heic,.heif"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                  multiple
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {/* File Previews */}
          {selectedFiles.length > 0 && (
            <div className="files-preview-container mb-2">
              {selectedFiles.map((file, index) => (
                <div
                  key={`file-${index}`}
                  className="file-preview mb-2 p-2 bg-light rounded d-flex align-items-center justify-content-between"
                >
                  <div className="d-flex align-items-center flex-grow-1">
                    {previewUrls[index] && (
                      <Image
                        src={previewUrls[index]}
                        width={60}
                        height={60}
                        className="me-3 rounded"
                        style={{ objectFit: "cover" }}
                        alt={`Preview ${index + 1}`}
                      />
                    )}
                    <div className="flex-grow-1">
                      <div
                        className="file-title text-truncate"
                        style={{ maxWidth: "300px" }}
                      >
                        {file.name}
                      </div>
                      <small className="file-size text-muted">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </small>
                    </div>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => clearFile(index)}
                    disabled={isSubmitting}
                    aria-label={`Remove ${file.name}`}
                  >
                    ×
                  </Button>
                </div>
              ))}
            </div>
          )}
          {/* Error message */}
          {fileError !== "" ? (
            <div className="error-alert with-filesize">
              <p className="error-message text-dange">{fileError}</p>
            </div>
          ) : (
            <></>
          )}

          {/* Character Count */}
          {postContent && (
            <div className="post-count text-end mb-2">
              <small className="text-muted">{postContent.length} / 5000</small>
            </div>
          )}

          {/* Poll Toggle Checkbox */}
          <div className="poll-checkbox-toggle">
            <label className="poll-checkbox-label">
              <input
                type="checkbox"
                checked={showPollForm}
                onChange={(e) => {
                  setShowPollForm(e.target.checked);
                  if (!e.target.checked) {
                    setPollQuestion("");
                    setPollOptions(["", ""]);
                    setIsMultiSelect(false);
                  }
                }}
                disabled={isSubmitting}
                className="poll-checkbox-input"
              />
              <span className="poll-checkbox-text">Add a poll</span>
            </label>
          </div>

          {/* Poll Section */}
          {showPollForm && (
            <div className="poll-form-section">
              <div className="poll-form-header">
                <div className="poll-form-title">
                  <span>Poll</span>
                </div>
                <button
                  type="button"
                  className="poll-close-btn"
                  aria-label="Remove poll"
                  onClick={() => {
                    setShowPollForm(false);
                    setPollQuestion("");
                    setPollOptions(["", ""]);
                    setIsMultiSelect(false);
                  }}
                >
                  <FiX size={15} />
                </button>
              </div>
              <div className="poll-form-body">
                <Form.Control
                  type="text"
                  placeholder="Ask your question..."
                  value={pollQuestion}
                  onChange={(e) => setPollQuestion(e.target.value)}
                  maxLength={300}
                  className="poll-question-input"
                  disabled={isSubmitting}
                />
                <div className="poll-options-list">
                  {pollOptions.map((opt, idx) => (
                    <div key={idx} className="poll-option-row">
                      <div className="option-number">{idx + 1}</div>
                      <Form.Control
                        type="text"
                        placeholder={`Option ${idx + 1}`}
                        value={opt}
                        onChange={(e) => {
                          const updated = [...pollOptions];
                          updated[idx] = e.target.value;
                          setPollOptions(updated);
                        }}
                        maxLength={100}
                        className="poll-option-input"
                        disabled={isSubmitting}
                      />
                      {pollOptions.length > 2 && (
                        <button
                          type="button"
                          className="option-remove-btn"
                          onClick={() => setPollOptions(pollOptions.filter((_, i) => i !== idx))}
                          disabled={isSubmitting}
                        >
                          <FiX size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {pollOptions.length < 6 && (
                  <button
                    type="button"
                    className="add-option-btn"
                    onClick={() => setPollOptions([...pollOptions, ""])}
                    disabled={isSubmitting}
                  >
                    + Add Option
                  </button>
                )}
                <div className="poll-multi-toggle">
                  <label className="poll-multi-label">
                    <input
                      type="checkbox"
                      checked={isMultiSelect}
                      onChange={(e) => setIsMultiSelect(e.target.checked)}
                      disabled={isSubmitting}
                      className="poll-multi-checkbox"
                    />
                    Allow multiple selections
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-buttons d-flex justify-content-between align-items-center gap-3 mt-3">
            <div className="d-flex gap-3">
              <Button
                variant={isAnonymous ? "primary" : "outline-primary"}
                className="anonymous-btn"
                onClick={() => setIsAnonymous(!isAnonymous)}
                disabled={isSubmitting}
              >
                {isAnonymous ? "✓ " : ""}Post Anonymously
              </Button>
            </div>
            {/* <Button
              type="submit"
              variant="primary"
              className="post-btn"
              disabled={!isFormValid}
            >
              {isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Posting...
                </>
              ) : (
                "Post"
              )}
            </Button> */}
            <Button
              type="submit"
              variant="primary"
              className="post-btn"
              disabled={!isFormValid}
            >
              {isSubmitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Posting...
                </>
              ) : (
                "Post"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CreatePostModal;
